import {
  ChevronRight, Info, Flame, MoonStar, HeartPulse, BedDouble,
  BatteryLow, Sparkle, Scale, Activity,
} from "lucide-react";

const ICONS = { Flame, MoonStar, HeartPulse, BedDouble, BatteryLow, Sparkle, Scale, Activity };

function BlockHeading({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-6 h-6 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
        <ChevronRight size={15} className="text-brand" />
      </span>
      <h3 className="font-serif text-lg font-bold text-ink">{children}</h3>
    </div>
  );
}

function Block({ block, index }) {
  const t = block.type;

  if (t === "paragraph") {
    return (
      <div data-testid={`learnblock-${index}`}>
        {block.title && <BlockHeading>{block.title}</BlockHeading>}
        <p className="text-ink-soft leading-relaxed">{block.text}</p>
      </div>
    );
  }

  if (t === "phases") {
    return (
      <div className="rounded-2xl border border-subtle p-5 bg-app" data-testid={`learnblock-${index}`}>
        <BlockHeading>{block.title}</BlockHeading>
        <div className="space-y-4">
          {block.items.map((it, i) => (
            <div key={i} className="pl-1">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
                <span className="font-semibold text-ink">{it.name}</span>
              </div>
              <p className="text-ink-soft text-sm leading-relaxed mt-1 pl-4">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (t === "symptom_grid") {
    return (
      <div data-testid={`learnblock-${index}`}>
        <BlockHeading>{block.title}</BlockHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {block.items.map((it, i) => {
            const Icon = ICONS[it.icon] || Activity;
            return (
              <div key={i} className="rounded-xl border border-subtle bg-app p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-7 h-7 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-brand" />
                  </span>
                  <span className="font-semibold text-ink text-sm">{it.name}</span>
                </div>
                <p className="text-ink-soft text-sm leading-relaxed">{it.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (t === "strategies") {
    return (
      <div data-testid={`learnblock-${index}`}>
        <BlockHeading>{block.title}</BlockHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {block.items.map((it, i) => (
            <div key={i} className="rounded-xl border border-subtle overflow-hidden bg-app">
              <div className="bg-sage px-4 py-2.5">
                <span className="font-semibold text-ink text-sm">{it.name}</span>
              </div>
              <p className="text-ink-soft text-sm leading-relaxed px-4 py-3">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (t === "callout") {
    return (
      <div className="rounded-2xl bg-sage/60 border border-subtle p-5" data-testid={`learnblock-${index}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-7 h-7 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
            <Info size={15} className="text-white" />
          </span>
          <h3 className="font-serif text-base font-bold text-ink">{block.title}</h3>
        </div>
        {block.text && <p className="text-ink-soft text-sm leading-relaxed">{block.text}</p>}
        {block.bullets?.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {block.bullets.map((b, i) => (
              <li key={i} className="flex gap-2 text-ink-soft text-sm">
                <span className="text-brand flex-shrink-0">•</span> {b}
              </li>
            ))}
          </ul>
        )}
        {block.footer && <p className="text-ink font-semibold text-sm mt-3">{block.footer}</p>}
      </div>
    );
  }

  if (t === "tips") {
    return (
      <div data-testid={`learnblock-${index}`}>
        <BlockHeading>{block.title}</BlockHeading>
        {block.intro && <p className="text-ink-soft leading-relaxed mb-2">{block.intro}</p>}
        <ul className="space-y-1.5">
          {block.bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-ink-soft text-sm">
              <span className="text-brand flex-shrink-0">•</span> {b}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (t === "checklist") {
    return (
      <div className="rounded-2xl border border-subtle p-5 bg-app" data-testid={`learnblock-${index}`}>
        <BlockHeading>{block.title}</BlockHeading>
        {block.intro && <p className="text-ink-soft text-sm leading-relaxed mb-3">{block.intro}</p>}
        <ul className="space-y-2.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2.5 text-ink-soft text-sm">
              <span className="w-4 h-4 rounded-[4px] border-2 border-[var(--brand)] flex-shrink-0 mt-0.5" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
        {block.tip && (
          <p className="text-ink-soft text-sm italic mt-4">
            <span className="font-semibold text-ink not-italic">Strategy Tip:</span> {block.tip}
          </p>
        )}
      </div>
    );
  }

  return null;
}

export default function LearnBlocks({ blocks }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="space-y-5" data-testid="learn-blocks">
      {blocks.map((block, i) => (
        <Block key={i} block={block} index={i} />
      ))}
    </div>
  );
}
